import { useEffect, useRef, useState } from 'react';

const metodologia = [
  {
    num: '01',
    icon: 'ri-gamepad-line',
    color: 'text-vermelho',
    bg: 'bg-vermelho',
    title: 'Aprender Brincando',
    desc: 'O lúdico é a principal ferramenta de aprendizagem. Por meio de jogos, brincadeiras e atividades criativas, as crianças absorvem conhecimento de forma natural e prazerosa.',
  },
  {
    num: '02',
    icon: 'ri-emotion-happy-line',
    color: 'text-azul-claro',
    bg: 'bg-azul-claro',
    title: 'Desenvolvimento Emocional',
    desc: 'Trabalhamos a inteligência emocional das crianças, ensinando-as a reconhecer e expressar sentimentos de forma saudável, construindo relações de empatia e respeito.',
  },
  {
    num: '03',
    icon: 'ri-team-line',
    color: 'text-azul-medio',
    bg: 'bg-azul-medio',
    title: 'Desenvolvimento Social',
    desc: 'Atividades em grupo estimulam a convivência harmoniosa, a cooperação e o respeito às diferenças, preparando as crianças para a vida em sociedade.',
  },
  {
    num: '04',
    icon: 'ri-brain-line',
    color: 'text-azul-escuro',
    bg: 'bg-azul-escuro',
    title: 'Desenvolvimento Cognitivo',
    desc: 'Estímulos adequados à faixa etária desenvolvem a linguagem, o raciocínio lógico, a criatividade e a curiosidade, criando bases sólidas para o aprendizado futuro.',
  },
  {
    num: '05',
    icon: 'ri-calendar-check-line',
    color: 'text-vermelho',
    bg: 'bg-vermelho',
    title: 'Rotina Estruturada',
    desc: 'A rotina traz segurança e previsibilidade às crianças. Com horários bem definidos para atividades, refeições e descanso, elas se desenvolvem com equilíbrio e tranquilidade.',
  },
  {
    num: '06',
    icon: 'ri-bookmark-heart-line',
    color: 'text-azul-medio',
    bg: 'bg-azul-medio',
    title: 'Valores Cristãos',
    desc: 'Integramos princípios cristãos de amor, respeito, honestidade e gratidão no dia a dia escolar, formando crianças com caráter, bondade e fé.',
  },
];

export default function Metodologia() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="metodologia" className="py-24 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block bg-vermelho/10 text-vermelho font-body font-700 text-sm px-4 py-1.5 rounded-full mb-4">
            Nossa Metodologia
          </span>
          <h4 id="metodologia-section">
            <a className="font-heading text-[#0D0D0D] text-3xl lg:text-4xl block">
              Como trabalhamos o desenvolvimento
            </a>
          </h4>
          <p className="font-body text-gray-500 text-base mt-4 max-w-xl mx-auto">
            Uma abordagem completa que cuida de cada aspecto da formação da criança, integrando aprendizado, emoção e valores.
          </p>
        </div>

        {/* Image + Cards */}
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Left image */}
          <div
            className={`lg:col-span-2 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div className="relative rounded-3xl overflow-hidden h-[500px]">
              <img
                src="https://storage.readdy-site.link/project_files/f6b15bb9-0a9f-496b-9ad9-42176d58e2ec/96247e39-5f66-4d63-bd34-f38daa21ba8f_20251110_092734.jpg?v=888692db8750132edcaff277e099c77d"
                alt="Metodologia lúdica da escola infantil Turminha da Alegria"
                title="Aprender brincando – escola infantil em Campinas"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-azul-escuro/50 to-transparent" />
              {/* Quote */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-5">
                <p className="font-body text-[#0D0D0D] text-sm italic leading-relaxed">
                  &ldquo;Brincar é a linguagem universal da criança. É através do brincar que ela aprende a viver.&rdquo;
                </p>
                <p className="font-body text-azul-medio text-xs font-700 mt-2">— Filosofia Turminha da Alegria</p>
              </div>
            </div>
          </div>

          {/* Right grid */}
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
            {metodologia.map((item, i) => (
              <div
                key={item.title}
                className={`group p-5 rounded-2xl border border-gray-100 hover:border-transparent hover:shadow-md hover:-translate-y-1 transition-all duration-300 bg-white ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${i * 80 + 300}ms` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${item.bg}/10 rounded-xl`}>
                    <i className={`${item.icon} ${item.color} text-lg`} />
                  </div>
                  <span className="font-heading text-gray-300 text-2xl">{item.num}</span>
                </div>
                <h5 className="font-body font-700 text-[#0D0D0D] text-base mb-2">{item.title}</h5>
                <p className="font-body text-gray-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
